import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { TemplateConfig } from '@/config/templates';

interface StorefrontContextType {
    themeTokens: TemplateConfig['theme'];
    personaConfig: NonNullable<TemplateConfig['personaConfig']>;
    croConfig: NonNullable<TemplateConfig['croConfig']>;
    resolvedData: any;
    template: TemplateConfig;
    store: any;
    /** Legacy (1–51) themes: demo injection + safe CTAs enabled in builder/preview */
    isLegacyTemplate?: boolean;
}

const StorefrontContext = createContext<StorefrontContextType | undefined>(undefined);

export const StorefrontProvider = ({
    template,
    store,
    resolvedData,
    isLegacyTemplate,
    children
}: {
    template: TemplateConfig;
    store: any;
    resolvedData: any;
    isLegacyTemplate?: boolean;
    children: ReactNode
}) => {
    const value = useMemo(() => {
        // Fallback for v1 templates or missing config
        const defaultPersonaConfig: NonNullable<TemplateConfig['personaConfig']> = {
            cardVariant: (template.persona?.toLowerCase() as any) || "standard",
            ctaStyle: "solid",
            imageRatio: "1:1",
            spacingScale: "normal",
            tone: "premium"
        };

        const defaultCroConfig: NonNullable<TemplateConfig['croConfig']> = {
            codBanner: true,      // Tunisia first
            stickyCart: true,
            urgencyBadges: false,
            whatsappCTA: true,
            trustBadges: true,
        };

        return {
            themeTokens: template.theme,
            personaConfig: template.personaConfig || defaultPersonaConfig,
            croConfig: template.croConfig || defaultCroConfig,
            resolvedData,
            template,
            store,
            isLegacyTemplate: Boolean(isLegacyTemplate),
        };
    }, [template, store, resolvedData, isLegacyTemplate]);

    return (
        <StorefrontContext.Provider value={value}>
            {children}
        </StorefrontContext.Provider>
    );
};

export const useStorefront = () => {
    const context = useContext(StorefrontContext);
    if (!context) {
        throw new Error('useStorefront must be used within StorefrontProvider');
    }
    return context;
};

/** Same context as `useStorefront` but returns `undefined` outside `StorefrontProvider` (safe for shared UI like CTAs). */
export const useOptionalStorefront = () => useContext(StorefrontContext);
